/**
 * Offline Rubber Band wrapper — pitch-preserved time-stretching and
 * pitch-shifting via a WASM port of the Rubber Band Library.
 *
 * Phase 1a of the Slicer overhaul (see plan): warp an AudioBuffer to
 * a target BPM (and/or pitch) without artifacts that come from naïve
 * `playbackRate` scaling. Result is cached by content key so a
 * subsequent warp with identical params is instant.
 *
 * Licensing: the bundled `rubberband-wasm` package ships under
 * GPLv2. That's fine for development. Before commercial release we
 * MUST swap to a commercial Rubber Band build (contact
 * info@breakfastquay.com). All callers route through this single
 * file so the swap is one source change.
 *
 * Reference: the Daninet demo worker
 * (node_modules/rubberband-wasm/demo/worker.js) is the canonical
 * offline flow — study → process → retrieve → free. We follow that
 * sequence, chunking by the engine's `samplesRequired` value.
 */

import { RubberBandInterface, RubberBandOption } from 'rubberband-wasm'
// Vite asset-url import so the WASM binary becomes a hashed file in
// dist/ and we fetch it at runtime rather than inlining it into the
// JS bundle. The `?url` suffix is a Vite feature; the ambient module
// declaration in `vite-env.d.ts` covers its typing.
import rubberbandWasmUrl from 'rubberband-wasm/dist/rubberband.wasm?url'

/** High-level warp modes mapped to sensible Rubber Band option
 *  combinations. Mirrors the Rubber Band command-line flags that
 *  jungle / DnB producers are used to reading about. */
export type WarpMode = 'beats' | 'tones' | 'texture' | 'complex'

export interface WarpParams {
  /** Source tempo in BPM. Used together with `targetBpm` to derive
   *  the time ratio. */
  sourceBpm?: number
  /** Target tempo in BPM. */
  targetBpm?: number
  /** Direct override: output duration / input duration. Takes
   *  precedence over the BPM-based ratio when provided. */
  timeRatio?: number
  /** Pitch shift in semitones. +12 = one octave up; -12 = one down.
   *  Independent of the time ratio. */
  pitchSemitones?: number
  /** Content-aware mode. `beats` (default) is transient-aware and
   *  tuned for drum loops — kicks stay punchy. `tones` preserves
   *  phase for melodic material. `texture` is the smoothest for
   *  pads. `complex` is a generic fallback. */
  mode?: WarpMode
}

/** Resolved parameters the engine actually runs with. Exposed for
 *  cache-key construction + diagnostics. */
interface ResolvedParams {
  timeRatio: number
  pitchScale: number
  mode: WarpMode
}

// ── Engine bootstrap ────────────────────────────────────────

let apiPromise: Promise<RubberBandInterface> | null = null

async function getApi(): Promise<RubberBandInterface> {
  if (apiPromise) return apiPromise
  apiPromise = (async () => {
    // `compileStreaming` is the fast path — the browser can start
    // compiling while the network transfer is still in flight. Falls
    // back to `compile` if the server doesn't serve the right MIME.
    let wasm: WebAssembly.Module
    try {
      wasm = await WebAssembly.compileStreaming(fetch(rubberbandWasmUrl))
    } catch {
      const res = await fetch(rubberbandWasmUrl)
      const bytes = await res.arrayBuffer()
      wasm = await WebAssembly.compile(bytes)
    }
    return RubberBandInterface.initialize(wasm)
  })()
  return apiPromise
}

// ── LRU cache ───────────────────────────────────────────────

interface CacheEntry {
  key: string
  buffer: AudioBuffer
  bytes: number
}

const CACHE_CAP_BYTES = 50 * 1024 * 1024
const cache: CacheEntry[] = []
let cachedBytes = 0

function keyFor(
  sourceFingerprint: string,
  params: ResolvedParams,
  sampleRate: number,
  channels: number,
): string {
  const { timeRatio, pitchScale, mode } = params
  // 6-digit rounding — anything finer is indistinguishable to the ear
  // and to Rubber Band, and stops sub-bit float noise from splitting
  // the cache on identical warps.
  const tr = timeRatio.toFixed(6)
  const ps = pitchScale.toFixed(6)
  return `${sourceFingerprint}:${sampleRate}:${channels}:${tr}:${ps}:${mode}`
}

function bytesOf(buffer: AudioBuffer): number {
  return buffer.length * buffer.numberOfChannels * 4
}

function cacheGet(key: string): AudioBuffer | null {
  const idx = cache.findIndex(e => e.key === key)
  if (idx < 0) return null
  // Bump to head (MRU).
  const [entry] = cache.splice(idx, 1)
  cache.unshift(entry)
  return entry.buffer
}

function cachePut(key: string, buffer: AudioBuffer) {
  const bytes = bytesOf(buffer)
  cache.unshift({ key, buffer, bytes })
  cachedBytes += bytes
  while (cachedBytes > CACHE_CAP_BYTES && cache.length > 1) {
    const dropped = cache.pop()
    if (dropped) cachedBytes -= dropped.bytes
  }
}

/** Exposed for tests + diagnostics only. */
export function __resetRubberbandCache() {
  cache.length = 0
  cachedBytes = 0
}

// ── Offline warp ────────────────────────────────────────────

/**
 * Warp an AudioBuffer. Returns a new AudioBuffer with the applied
 * time-stretch + pitch-shift. Original buffer is not mutated.
 *
 * @param source the decoded AudioBuffer to warp
 * @param params WarpParams (see type above)
 * @param sourceFingerprint a stable id for the *content* of `source`
 *        — usually a `sampleId`. Used as the cache key. Don't pass
 *        a hash of the buffer PCM every call; the whole point of the
 *        cache is to make repeat warps O(1).
 */
export async function warpOffline(
  source: AudioBuffer,
  params: WarpParams,
  sourceFingerprint: string,
): Promise<AudioBuffer> {
  const resolved = resolveParams(params)
  const key = keyFor(
    sourceFingerprint,
    resolved,
    source.sampleRate,
    source.numberOfChannels,
  )
  const cached = cacheGet(key)
  if (cached) return cached

  // Short-circuit a no-op warp — skips WASM load + all the memory
  // copying. Common case when `sourceBpm === targetBpm` and there's
  // no pitch shift, which happens on the fire-and-forget "maybe warp
  // everything to project BPM" path.
  if (isUnity(resolved)) {
    cachePut(key, source)
    return source
  }

  const out = await runWarp(source, resolved)
  cachePut(key, out)
  return out
}

function resolveParams(params: WarpParams): ResolvedParams {
  let timeRatio = 1
  if (params.timeRatio && params.timeRatio > 0) {
    timeRatio = params.timeRatio
  } else if (
    params.sourceBpm &&
    params.sourceBpm > 0 &&
    params.targetBpm &&
    params.targetBpm > 0
  ) {
    // Output-duration-per-input-duration. A 90 BPM loop played at 140
    // BPM compresses — output is 90/140 the length, so timeRatio < 1.
    timeRatio = params.sourceBpm / params.targetBpm
  }
  const pitchScale = Math.pow(2, (params.pitchSemitones ?? 0) / 12)
  return { timeRatio, pitchScale, mode: params.mode ?? 'beats' }
}

function isUnity({ timeRatio, pitchScale }: ResolvedParams): boolean {
  return Math.abs(timeRatio - 1) < 1e-6 && Math.abs(pitchScale - 1) < 1e-6
}

function optionsFor(mode: WarpMode): number {
  // Rubber Band's option bitfield. The "R3" (Finer) engine is higher
  // quality but 3× the CPU; offline can absorb that so we always pick
  // it here. Mode-specific detector / transient / phase choices are
  // the ones the Rubber Band command-line guide recommends.
  let opts = RubberBandOption.RubberBandOptionProcessOffline
  opts |= RubberBandOption.RubberBandOptionEngineFiner
  switch (mode) {
    case 'beats':
      opts |= RubberBandOption.RubberBandOptionTransientsCrisp
      opts |= RubberBandOption.RubberBandOptionDetectorPercussive
      opts |= RubberBandOption.RubberBandOptionPhaseIndependent
      break
    case 'tones':
      opts |= RubberBandOption.RubberBandOptionTransientsSmooth
      opts |= RubberBandOption.RubberBandOptionDetectorSoft
      opts |= RubberBandOption.RubberBandOptionPhaseLaminar
      break
    case 'texture':
      opts |= RubberBandOption.RubberBandOptionTransientsSmooth
      opts |= RubberBandOption.RubberBandOptionDetectorCompound
      opts |= RubberBandOption.RubberBandOptionPhaseLaminar
      opts |= RubberBandOption.RubberBandOptionWindowLong
      break
    case 'complex':
    default:
      opts |= RubberBandOption.RubberBandOptionTransientsMixed
      opts |= RubberBandOption.RubberBandOptionDetectorCompound
      opts |= RubberBandOption.RubberBandOptionPhaseLaminar
      break
  }
  return opts
}

async function runWarp(
  source: AudioBuffer,
  params: ResolvedParams,
): Promise<AudioBuffer> {
  const api = await getApi()
  const { sampleRate, numberOfChannels: channels, length: inLength } = source

  // Extract channel Float32Arrays once.
  const channelBuffers: Float32Array[] = []
  for (let c = 0; c < channels; c++) {
    // `getChannelData` returns a reference; copy so we don't mutate
    // the user's buffer if the engine ever writes back in-place.
    channelBuffers.push(new Float32Array(source.getChannelData(c)))
  }

  const options = optionsFor(params.mode)
  const state = api.rubberband_new(
    sampleRate,
    channels,
    options,
    params.timeRatio,
    params.pitchScale,
  )

  try {
    api.rubberband_set_expected_input_duration(state, inLength)
    const samplesRequired = api.rubberband_get_samples_required(state)

    // Allocate the channel-pointer table + per-channel work buffers
    // once. The same buffers get reused across study + process passes
    // to avoid thrashing the WASM heap.
    const channelArrayPtr = api.malloc(channels * 4)
    const channelDataPtrs: number[] = []
    for (let c = 0; c < channels; c++) {
      const ptr = api.malloc(samplesRequired * 4)
      channelDataPtrs.push(ptr)
      api.memWritePtr(channelArrayPtr + c * 4, ptr)
    }

    // Output allocation. Rubber Band's size hint is `in × timeRatio`;
    // add a small cushion for rounding + transient padding.
    const outSamplesEstimate = Math.ceil(inLength * params.timeRatio + 64)
    const outputChannels: Float32Array[] = []
    for (let c = 0; c < channels; c++) {
      outputChannels.push(new Float32Array(outSamplesEstimate))
    }
    let written = 0

    try {
      // Pass 1 — study: feed the entire input so the engine can plan
      // transient placement before processing. Required for offline
      // mode.
      for (let read = 0; read < inLength; ) {
        const remaining = Math.min(samplesRequired, inLength - read)
        for (let c = 0; c < channels; c++) {
          api.memWrite(
            channelDataPtrs[c],
            channelBuffers[c].subarray(read, read + remaining),
          )
        }
        const isFinal = read + remaining >= inLength
        api.rubberband_study(state, channelArrayPtr, remaining, isFinal ? 1 : 0)
        read += remaining
      }

      // Pass 2 — process + drain. Retrieve whatever's ready after
      // each process() pass to keep the engine's internal buffer
      // from overflowing.
      const drain = (final: boolean) => {
        for (;;) {
          const available = api.rubberband_available(state)
          if (available < 1) break
          if (!final && available < samplesRequired) break
          const take = Math.min(samplesRequired, available)
          const recv = api.rubberband_retrieve(state, channelArrayPtr, take)
          if (recv <= 0) break
          if (written + recv > outSamplesEstimate) {
            // Extremely unlikely — output ended up longer than our
            // cushion. Grow the output arrays.
            const grown = outSamplesEstimate * 2
            for (let c = 0; c < channels; c++) {
              const next = new Float32Array(grown)
              next.set(outputChannels[c])
              outputChannels[c] = next
            }
          }
          for (let c = 0; c < channels; c++) {
            outputChannels[c].set(
              api.memReadF32(channelDataPtrs[c], recv),
              written,
            )
          }
          written += recv
        }
      }

      for (let read = 0; read < inLength; ) {
        const remaining = Math.min(samplesRequired, inLength - read)
        for (let c = 0; c < channels; c++) {
          api.memWrite(
            channelDataPtrs[c],
            channelBuffers[c].subarray(read, read + remaining),
          )
        }
        const isFinal = read + remaining >= inLength
        api.rubberband_process(
          state,
          channelArrayPtr,
          remaining,
          isFinal ? 1 : 0,
        )
        read += remaining
        drain(false)
      }
      drain(true)
    } finally {
      for (const ptr of channelDataPtrs) api.free(ptr)
      api.free(channelArrayPtr)
    }

    // Build the output AudioBuffer. Must use the same AudioContext
    // family that will play the buffer back; callers pass through a
    // decode-context, so any modern AudioContext works.
    const ctx = getOfflineCtx(sampleRate, written, channels)
    const outBuffer = ctx.createBuffer(channels, written, sampleRate)
    for (let c = 0; c < channels; c++) {
      outBuffer.copyToChannel(outputChannels[c].subarray(0, written), c)
    }
    return outBuffer
  } finally {
    api.rubberband_delete(state)
  }
}

// Use a lightweight OfflineAudioContext as the buffer factory — no
// rendering actually happens; `createBuffer` is the only call we
// need and OAC is cheaper to construct than a full AudioContext.
//
// OfflineAudioContext doesn't expose `numberOfChannels` as a
// readable property on the instance (it's in the constructor options
// only), so we cache by the latest constructor args instead.
let offlineCtx: OfflineAudioContext | null = null
let offlineCtxChannels = 0
function getOfflineCtx(
  sampleRate: number,
  length: number,
  channels: number,
): OfflineAudioContext {
  if (
    offlineCtx &&
    offlineCtx.sampleRate === sampleRate &&
    offlineCtxChannels >= channels
  ) {
    return offlineCtx
  }
  const ch = Math.max(channels, 2)
  offlineCtx = new OfflineAudioContext(ch, Math.max(length, 1), sampleRate)
  offlineCtxChannels = ch
  return offlineCtx
}
